import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Trash2, Inbox, RefreshCw, Heart, LogOut, Video, BookOpen, Calendar, FolderOpen, Mail, Plus, Loader2, FileText, Globe, Users, BarChart3, Eye, Monitor, MapPin, ChevronDown, ChevronUp, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [prayers, setPrayers] = useState([]);
  const [sermons, setSermons] = useState([]);
  const [devotionals, setDevotionals] = useState([]);
  const [events, setEvents] = useState([]);
  const [resources, setResources] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [addonsList, setAddonsList] = useState([]);
  const [schoolsList, setSchoolsList] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const [webStats, setWebStats] = useState(null);
  const [statsHours, setStatsHours] = useState(24);
  const [loading, setLoading] = useState(true);

  // Form states
  const [sermonForm, setSermonForm] = useState({ title: "", youtube_url: "", description: "" });
  const [devoForm, setDevoForm] = useState({ verse_ref: "" });
  const [eventForm, setEventForm] = useState({ title: "", description: "", date: "", time: "" });
  const [resourceForm, setResourceForm] = useState({ title: "", description: "", url: "" });
  const [schoolForm, setSchoolForm] = useState({ name: "", description: "", website_url: "", tags: "" });
  const [blogTopic, setBlogTopic] = useState("");
  const [blogGenerating, setBlogGenerating] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, p, s, d, e, r, n, intake, addonsRes, schoolsRes, blogRes, liveRes, statsRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/prayer-request`),
        axios.get(`${API}/sermons`),
        axios.get(`${API}/devotionals`),
        axios.get(`${API}/events`),
        axios.get(`${API}/resources`),
        axios.get(`${API}/newsletter`),
        axios.get(`${API}/intake`),
        axios.get(`${API}/addons`),
        axios.get(`${API}/schools/all`),
        axios.get(`${API}/blog/all`),
        axios.get(`${API}/presence/details`).catch(() => ({ data: [] })),
        axios.get(`${API}/presence/history?hours=24`).catch(() => ({ data: { sessions: [] } })),
      ]);
      setSubmissions(c.data);
      setPrayers(p.data);
      setSermons(s.data);
      setDevotionals(d.data);
      setEvents(e.data);
      setResources(r.data);
      setNewsletter(n.data);
      setIntakes(intake.data);
      setAddonsList(addonsRes.data);
      setSchoolsList(schoolsRes.data);
      setBlogPosts(blogRes.data);
      setLiveUsers(liveRes.data);
      setWebStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) fetchAll(); }, [user]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  // CRUD helpers
  const deleteItem = async (endpoint, id, setter) => {
    try {
      await axios.delete(`${API}/${endpoint}/${id}`);
      setter(prev => prev.filter(i => i.id !== id));
      toast.success("Deleted.");
    } catch { toast.error("Failed to delete."); }
  };

  const addSermon = async (e) => {
    e.preventDefault();
    if (!sermonForm.title || !sermonForm.youtube_url) return toast.error("Title and YouTube URL required.");
    try {
      const res = await axios.post(`${API}/sermons`, sermonForm);
      setSermons(prev => [res.data, ...prev]);
      setSermonForm({ title: "", youtube_url: "", description: "" });
      toast.success("Sermon added!");
    } catch { toast.error("Failed to add sermon."); }
  };

  const generateDevotional = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/devotionals/generate`, devoForm);
      setDevotionals(prev => [res.data, ...prev]);
      setDevoForm({ verse_ref: "" });
      toast.success("Devotional generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate.");
    } finally { setGenerating(false); }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.date) return toast.error("Title and date required.");
    try {
      const res = await axios.post(`${API}/events`, eventForm);
      setEvents(prev => [...prev, res.data]);
      setEventForm({ title: "", description: "", date: "", time: "" });
      toast.success("Event added!");
    } catch { toast.error("Failed to add event."); }
  };

  const addResource = async (e) => {
    e.preventDefault();
    if (!resourceForm.title) return toast.error("Title required.");
    try {
      const res = await axios.post(`${API}/resources`, resourceForm);
      setResources(prev => [res.data, ...prev]);
      setResourceForm({ title: "", description: "", url: "" });
      toast.success("Resource added!");
    } catch { toast.error("Failed to add resource."); }
  };

  const addSchool = async (e) => {
    e.preventDefault();
    if (!schoolForm.name || !schoolForm.website_url) return toast.error("Name and website URL required.");
    try {
      const tags = schoolForm.tags ? schoolForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [];
      const res = await axios.post(`${API}/schools`, { ...schoolForm, tags });
      setSchoolsList(prev => [...prev, res.data]);
      setSchoolForm({ name: "", description: "", website_url: "", tags: "" });
      toast.success("School added!");
    } catch { toast.error("Failed to add school."); }
  };

  if (authLoading) return <div className="min-h-[50vh] flex items-center justify-center text-[#595959]">Loading...</div>;
  if (!user) return null;

  return (
    <div data-testid="admin-page">
      <section className="bg-[#1C2E4A] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-2">Admin</p>
            <h1 data-testid="admin-headline" className="font-serif text-2xl sm:text-3xl font-bold text-white">Dashboard</h1>
          </div>
          <button data-testid="admin-logout-btn" onClick={handleLogout} className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </section>

      <section className="py-8 md:py-12 bg-white min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-20"><Loader2 className="w-6 h-6 text-[#C8A97E] animate-spin mx-auto" /></div>
          ) : (
            <Tabs defaultValue="orders" className="w-full">
              <TabsList className="mb-6 flex-wrap h-auto gap-1">
                <TabsTrigger value="orders" data-testid="tab-orders"><FileText className="w-3.5 h-3.5 mr-1" />Orders ({intakes.length})</TabsTrigger>
                <TabsTrigger value="sermons" data-testid="tab-sermons"><Video className="w-3.5 h-3.5 mr-1" />Sermons ({sermons.length})</TabsTrigger>
                <TabsTrigger value="devotionals" data-testid="tab-devotionals"><BookOpen className="w-3.5 h-3.5 mr-1" />Devotionals ({devotionals.length})</TabsTrigger>
                <TabsTrigger value="events" data-testid="tab-events"><Calendar className="w-3.5 h-3.5 mr-1" />Events ({events.length})</TabsTrigger>
                <TabsTrigger value="resources" data-testid="tab-resources"><FolderOpen className="w-3.5 h-3.5 mr-1" />Resources ({resources.length})</TabsTrigger>
                <TabsTrigger value="contacts" data-testid="tab-contacts"><Mail className="w-3.5 h-3.5 mr-1" />Contacts ({submissions.length})</TabsTrigger>
                <TabsTrigger value="prayers" data-testid="tab-prayers"><Heart className="w-3.5 h-3.5 mr-1" />Prayers ({prayers.length})</TabsTrigger>
                <TabsTrigger value="newsletter" data-testid="tab-newsletter"><Mail className="w-3.5 h-3.5 mr-1" />Newsletter ({newsletter.length})</TabsTrigger>
                <TabsTrigger value="addons" data-testid="tab-addons">Pricing/Add-ons</TabsTrigger>
                <TabsTrigger value="schools" data-testid="tab-schools"><Globe className="w-3.5 h-3.5 mr-1" />Schools ({schoolsList.length})</TabsTrigger>
                <TabsTrigger value="blog" data-testid="tab-blog"><BookOpen className="w-3.5 h-3.5 mr-1" />Blog ({blogPosts.length})</TabsTrigger>
                <TabsTrigger value="live" data-testid="tab-live"><Users className="w-3.5 h-3.5 mr-1" />Live ({liveUsers.length})</TabsTrigger>
                <TabsTrigger value="stats" data-testid="tab-stats"><BarChart3 className="w-3.5 h-3.5 mr-1" />Web Stats</TabsTrigger>
              </TabsList>

              {/* ORDERS / INTAKES */}
              <TabsContent value="orders">
                {intakes.length === 0 ? <p className="text-center text-[#595959] py-10">No orders yet.</p> : (
                  <div className="space-y-3">
                    {intakes.map(intake => {
                      const isOpen = expandedOrder === intake.id;
                      const name = intake.customer_info?.full_name || "Unknown";
                      const email = intake.customer_info?.email || "—";
                      const phone = intake.customer_info?.phone || "—";
                      const school = intake.school_info?.school_name || "Unnamed School";
                      const domain = intake.domain_info?.domain1 || "—";
                      const date = intake.created_at ? new Date(intake.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
                      const paid = intake.payment_status === "paid";

                      return (
                        <div key={intake.id} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] overflow-hidden">
                          {/* Header row — always visible */}
                          <div
                            className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#F5F2ED] transition-colors"
                            onClick={() => setExpandedOrder(isOpen ? null : intake.id)}
                            data-testid={`order-row-${intake.id}`}
                          >
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <ChevronDown className={`w-4 h-4 text-[#595959] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm text-[#1C2E4A] truncate">{school}</p>
                                <p className="text-xs text-[#595959] truncate">{name} &middot; {email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-4">
                              <span className="text-xs text-[#595959]/60 hidden sm:inline">{date}</span>
                              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${paid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                                {paid ? "Paid" : "Unpaid"}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteItem("intake", intake.id, setIntakes); }}
                                data-testid={`delete-order-${intake.id}`}
                                className="text-[#595959]/40 hover:text-red-500 p-1 transition-colors"
                                title="Delete order"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Expanded content */}
                          {isOpen && (
                            <div className="border-t border-[#E5E2DC] bg-white px-5 py-5">
                              {/* Contact & School Info */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                  <h4 className="text-xs uppercase tracking-wider text-[#C8A97E] font-semibold mb-3">Contact Information</h4>
                                  <div className="space-y-2 text-sm">
                                    <InfoRow label="Name" value={name} />
                                    <InfoRow label="Email" value={email} />
                                    <InfoRow label="Phone" value={phone} />
                                    <InfoRow label="Address" value={[intake.customer_info?.address, intake.customer_info?.city, intake.customer_info?.state, intake.customer_info?.zip].filter(Boolean).join(", ") || "—"} />
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-xs uppercase tracking-wider text-[#C8A97E] font-semibold mb-3">School Information</h4>
                                  <div className="space-y-2 text-sm">
                                    <InfoRow label="School Name" value={school} />
                                    <InfoRow label="Domain" value={domain} />
                                    <InfoRow label="Church/Ministry" value={intake.school_info?.church_name || "—"} />
                                    <InfoRow label="Denomination" value={intake.school_info?.denomination || "—"} />
                                    <InfoRow label="Grades" value={intake.school_info?.grades_offered || "—"} />
                                  </div>
                                </div>
                              </div>

                              {/* Order Details & Add-ons */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                  <h4 className="text-xs uppercase tracking-wider text-[#C8A97E] font-semibold mb-3">Order Details</h4>
                                  <div className="space-y-2 text-sm">
                                    <InfoRow label="Date" value={date} />
                                    <InfoRow label="Payment" value={paid ? "Paid" : "Unpaid"} highlight={paid ? "green" : "yellow"} />
                                    <InfoRow label="Status" value={intake.status || "pending"} />
                                    <InfoRow label="Total" value={intake.total_paid ? `$${intake.total_paid.toLocaleString()}` : "—"} />
                                  </div>
                                </div>
                                {intake.selected_addons && intake.selected_addons.length > 0 && (
                                  <div>
                                    <h4 className="text-xs uppercase tracking-wider text-[#C8A97E] font-semibold mb-3">Selected Add-ons</h4>
                                    <div className="flex flex-wrap gap-1.5">
                                      {intake.selected_addons.map((addon, j) => (
                                        <span key={j} className="text-xs bg-[#1C2E4A]/5 text-[#1C2E4A] px-2.5 py-1 rounded-full">{typeof addon === "string" ? addon : addon.name || addon.id}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Domain & Email */}
                              <DataSection title="Domain & Email" data={intake.domain_info} />

                              {/* Branding — form saves as "branding" */}
                              <DataSection title="Branding" data={intake.branding || intake.branding_info} />

                              {/* School Pricing */}
                              <DataSection title="School Pricing" data={intake.school_pricing} />

                              {/* School Year */}
                              <DataSection title="School Year" data={intake.school_year} />

                              {/* Enrollment — form saves as "enrollment_fields" */}
                              <DataSection title="Enrollment" data={intake.enrollment_fields || intake.enrollment_info} />

                              {/* Policies — form saves as "policies" */}
                              <DataSection title="Policies" data={intake.policies || intake.policy_info} />

                              {/* Documents */}
                              <DataSection title="Documents" data={intake.document_info} />

                              {/* Uploads */}
                              <DataSection title="Uploads" data={intake.uploads} />

                              {/* Website Content */}
                              <DataSection title="Website Content" data={intake.website_content} />

                              {/* Emails */}
                              <DataSection title="Email Setup" data={intake.emails} />

                              {/* Administrators */}
                              {intake.administrators && intake.administrators.length > 0 && (
                                <div className="mb-6">
                                  <h4 className="text-xs uppercase tracking-wider text-[#C8A97E] font-semibold mb-3">Administrators</h4>
                                  <div className="space-y-3">
                                    {intake.administrators.map((admin, ai) => (
                                      <div key={ai} className="bg-[#FAF9F6] rounded-lg p-3 border border-[#E5E2DC]">
                                        <p className="text-sm font-medium text-[#1C2E4A] mb-1">Admin {ai + 1}</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                          {Object.entries(admin).map(([k, v]) => v ? (
                                            <InfoRow key={k} label={k.replace(/_/g, " ")} value={String(v)} />
                                          ) : null)}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {intake.signature && (
                                <div className="mb-6">
                                  <h4 className="text-xs uppercase tracking-wider text-[#C8A97E] font-semibold mb-3">Signature</h4>
                                  <p className="text-sm text-[#1C2E4A] font-medium italic">{intake.signature}</p>
                                </div>
                              )}

                              {/* Actions */}
                              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#E5E2DC]">
                                {/* Mark as Paid / Paid */}
                                {!paid ? (
                                  <>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          await axios.patch(`${API}/intake/${intake.id}`, { payment_status: "paid" });
                                          setIntakes(prev => prev.map(i => i.id === intake.id ? {...i, payment_status: "paid"} : i));
                                          toast.success("Order marked as paid!");
                                        } catch { toast.error("Failed to update."); }
                                      }}
                                      data-testid={`mark-paid-${intake.id}`}
                                      className="text-xs bg-green-600 text-white hover:bg-green-700 px-4 py-1.5 rounded-md font-medium flex items-center gap-1.5 transition-colors"
                                    >
                                      Mark as Paid
                                    </button>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const res = await axios.post(`${API}/intake/${intake.id}/send-payment-link`, { origin_url: window.location.origin });
                                          toast.success(res.data.message);
                                        } catch (err) { toast.error(err.response?.data?.detail || "Failed to send link."); }
                                      }}
                                      data-testid={`send-payment-link-${intake.id}`}
                                      className="text-xs bg-[#C8A97E] text-white hover:bg-[#B3956A] px-4 py-1.5 rounded-md font-medium flex items-center gap-1.5 transition-colors"
                                    >
                                      <Mail className="w-3.5 h-3.5" /> Send Payment Link
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await axios.patch(`${API}/intake/${intake.id}`, { payment_status: "unpaid" });
                                        setIntakes(prev => prev.map(i => i.id === intake.id ? {...i, payment_status: "unpaid"} : i));
                                        toast.success("Order marked as unpaid.");
                                      } catch { toast.error("Failed to update."); }
                                    }}
                                    className="text-xs bg-green-100 text-green-700 hover:bg-green-200 px-4 py-1.5 rounded-md font-medium flex items-center gap-1.5 transition-colors"
                                  >
                                    Paid (click to undo)
                                  </button>
                                )}
                                {/* Publish */}
                                {intake.published_to_schools ? (
                                  <span className="text-xs text-green-600 font-medium flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Published to Our Schools</span>
                                ) : (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const schoolName = intake.school_info?.school_name || "Unnamed School";
                                      const dm = intake.domain_info?.domain1 || "";
                                      const url = dm ? (dm.startsWith("http") ? dm : `https://${dm}`) : "";
                                      if (!schoolName || !url) return toast.error("School name and domain required to publish.");
                                      try {
                                        const res = await axios.post(`${API}/schools`, {
                                          name: schoolName,
                                          description: `${schoolName} is a church school built and powered by ACS Virtual Church.`,
                                          website_url: url,
                                          tags: [],
                                        });
                                        setSchoolsList(prev => [...prev, res.data]);
                                        await axios.patch(`${API}/intake/${intake.id}`, { published_to_schools: true });
                                        setIntakes(prev => prev.map(i => i.id === intake.id ? {...i, published_to_schools: true} : i));
                                        toast.success(`${schoolName} published to Our Schools!`);
                                      } catch { toast.error("Failed to publish."); }
                                    }}
                                    data-testid={`publish-school-${intake.id}`}
                                    className="text-xs bg-[#C8A97E] text-white hover:bg-[#B3956A] px-4 py-1.5 rounded-md font-medium flex items-center gap-1.5 transition-colors"
                                  >
                                    <Globe className="w-3.5 h-3.5" /> Publish to Our Schools
                                  </button>
                                )}
                                {/* Delete */}
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteItem("intake", intake.id, setIntakes); }}
                                  className="text-xs text-red-500 hover:text-red-600 px-4 py-1.5 rounded-md font-medium flex items-center gap-1.5 border border-red-200 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Delete Order
                                </button>
                                {/* Create Customer Account */}
                                {!intake.customer_account ? (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const custEmail = intake.customer_info?.email;
                                      const custName = intake.customer_info?.full_name;
                                      if (!custEmail) return toast.error("No email on this order.");
                                      const pw = prompt(`Create customer login for ${custEmail}\n\nEnter a password for them:`);
                                      if (!pw || pw.length < 6) return toast.error("Password must be at least 6 characters.");
                                      try {
                                        await axios.post(`${API}/customers/create`, { intake_id: intake.id, email: custEmail, password: pw, name: custName || "" });
                                        setIntakes(prev => prev.map(i => i.id === intake.id ? {...i, customer_account: custEmail} : i));
                                        toast.success(`Customer account created! ${custEmail} can now log in at /customer`);
                                      } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
                                    }}
                                    className="text-xs bg-[#1C2E4A] text-white hover:bg-[#152238] px-4 py-1.5 rounded-md font-medium flex items-center gap-1.5 transition-colors"
                                  >
                                    <Users className="w-3.5 h-3.5" /> Create Customer Login
                                  </button>
                                ) : (
                                  <span className="text-xs text-green-600 font-medium flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Login: {intake.customer_account}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* SERMONS */}
              <TabsContent value="sermons">
                <form onSubmit={addSermon} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 mb-6">
                  <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Add Sermon</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm mb-1 block">Title</Label>
                      <Input value={sermonForm.title} onChange={e => setSermonForm({...sermonForm, title: e.target.value})} placeholder="Sermon title" className="border-[#E5E2DC] bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">YouTube URL</Label>
                      <Input value={sermonForm.youtube_url} onChange={e => setSermonForm({...sermonForm, youtube_url: e.target.value})} placeholder="https://youtube.com/watch?v=..." className="border-[#E5E2DC] bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Description (optional)</Label>
                      <Input value={sermonForm.description} onChange={e => setSermonForm({...sermonForm, description: e.target.value})} placeholder="Brief description" className="border-[#E5E2DC] bg-white" />
                    </div>
                  </div>
                  <button type="submit" className="mt-4 bg-[#1C2E4A] text-white hover:bg-[#152238] px-6 py-2 rounded-md text-sm font-medium">Add Sermon</button>
                </form>
                {sermons.length === 0 ? <p className="text-center text-[#595959] py-10">No sermons yet. Add one above.</p> : (
                  <div className="space-y-3">
                    {sermons.map(s => (
                      <div key={s.id} className="flex items-center justify-between bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4">
                        <div>
                          <p className="font-medium text-sm text-[#1C2E4A]">{s.title}</p>
                          <p className="text-xs text-[#595959] truncate max-w-md">{s.youtube_url}</p>
                        </div>
                        <button onClick={() => deleteItem("sermons", s.id, setSermons)} className="text-[#595959] hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* DEVOTIONALS */}
              <TabsContent value="devotionals">
                <form onSubmit={generateDevotional} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 mb-6">
                  <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Generate Devotional (AI)</h3>
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <Label className="text-sm mb-1 block">Bible Verse (optional - leave blank for random)</Label>
                      <Input value={devoForm.verse_ref} onChange={e => setDevoForm({...devoForm, verse_ref: e.target.value})} placeholder="e.g. John 3:16, Psalm 23" className="border-[#E5E2DC] bg-white" />
                    </div>
                    <button type="submit" disabled={generating} className="bg-[#C8A97E] text-white hover:bg-[#B3956A] px-6 py-2 rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2">
                      {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</> : "Generate"}
                    </button>
                  </div>
                </form>
                {devotionals.length === 0 ? <p className="text-center text-[#595959] py-10">No devotionals yet. Generate one above.</p> : (
                  <div className="space-y-3">
                    {devotionals.map(d => (
                      <div key={d.id} className="flex items-start justify-between bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4">
                        <div className="flex-1 mr-4">
                          <p className="font-medium text-sm text-[#1C2E4A]">{d.title}</p>
                          <p className="text-xs text-[#C8A97E]">{d.verse_ref}</p>
                          <p className="text-xs text-[#595959] mt-1 line-clamp-2">{d.message}</p>
                        </div>
                        <button onClick={() => deleteItem("devotionals", d.id, setDevotionals)} className="text-[#595959] hover:text-red-500 p-1 shrink-0"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* EVENTS */}
              <TabsContent value="events">
                <form onSubmit={addEvent} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 mb-6">
                  <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Add Event</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm mb-1 block">Title</Label>
                      <Input value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} placeholder="Event name" className="border-[#E5E2DC] bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Date</Label>
                      <Input type="date" value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})} className="border-[#E5E2DC] bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Time (optional)</Label>
                      <Input value={eventForm.time} onChange={e => setEventForm({...eventForm, time: e.target.value})} placeholder="e.g. 7:00 PM EST" className="border-[#E5E2DC] bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Description</Label>
                      <Input value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} placeholder="Brief description" className="border-[#E5E2DC] bg-white" />
                    </div>
                  </div>
                  <button type="submit" className="mt-4 bg-[#1C2E4A] text-white hover:bg-[#152238] px-6 py-2 rounded-md text-sm font-medium">Add Event</button>
                </form>
                {events.length === 0 ? <p className="text-center text-[#595959] py-10">No events yet. Add one above.</p> : (
                  <div className="space-y-3">
                    {events.map(ev => (
                      <div key={ev.id} className="flex items-center justify-between bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4">
                        <div>
                          <p className="font-medium text-sm text-[#1C2E4A]">{ev.title}</p>
                          <p className="text-xs text-[#595959]">{ev.date} {ev.time && `at ${ev.time}`}</p>
                        </div>
                        <button onClick={() => deleteItem("events", ev.id, setEvents)} className="text-[#595959] hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* RESOURCES */}
              <TabsContent value="resources">
                <form onSubmit={addResource} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 mb-6">
                  <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Add Resource</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm mb-1 block">Title</Label>
                      <Input value={resourceForm.title} onChange={e => setResourceForm({...resourceForm, title: e.target.value})} placeholder="Resource name" className="border-[#E5E2DC] bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">URL (optional)</Label>
                      <Input value={resourceForm.url} onChange={e => setResourceForm({...resourceForm, url: e.target.value})} placeholder="https://..." className="border-[#E5E2DC] bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Description</Label>
                      <Input value={resourceForm.description} onChange={e => setResourceForm({...resourceForm, description: e.target.value})} placeholder="Brief description" className="border-[#E5E2DC] bg-white" />
                    </div>
                  </div>
                  <button type="submit" className="mt-4 bg-[#1C2E4A] text-white hover:bg-[#152238] px-6 py-2 rounded-md text-sm font-medium">Add Resource</button>
                </form>
                {resources.length === 0 ? <p className="text-center text-[#595959] py-10">No resources yet. Add one above.</p> : (
                  <div className="space-y-3">
                    {resources.map(r => (
                      <div key={r.id} className="flex items-center justify-between bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4">
                        <div>
                          <p className="font-medium text-sm text-[#1C2E4A]">{r.title}</p>
                          <p className="text-xs text-[#595959]">{r.description}</p>
                        </div>
                        <button onClick={() => deleteItem("resources", r.id, setResources)} className="text-[#595959] hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* CONTACTS */}
              <TabsContent value="contacts">
                {submissions.length === 0 ? <div className="text-center py-10"><Inbox className="w-10 h-10 text-[#E5E2DC] mx-auto mb-3" /><p className="text-[#595959]">No contact submissions.</p></div> : (
                  <div className="border border-[#E5E2DC] rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader><TableRow className="bg-[#FAF9F6]">
                        <TableHead className="font-semibold">Name</TableHead>
                        <TableHead className="font-semibold">Email</TableHead>
                        <TableHead className="font-semibold hidden md:table-cell">Interest</TableHead>
                        <TableHead className="font-semibold hidden lg:table-cell">Message</TableHead>
                        <TableHead className="font-semibold">Date</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {submissions.map(s => (
                          <TableRow key={s.id}>
                            <TableCell className="font-medium">{s.name}</TableCell>
                            <TableCell className="text-[#595959]">{s.email}</TableCell>
                            <TableCell className="text-[#595959] hidden md:table-cell capitalize">{s.interest?.replace("-"," ") || "—"}</TableCell>
                            <TableCell className="text-[#595959] hidden lg:table-cell max-w-xs truncate">{s.message}</TableCell>
                            <TableCell className="text-[#595959] text-sm">{new Date(s.timestamp).toLocaleDateString()}</TableCell>
                            <TableCell><button onClick={() => deleteItem("contact", s.id, setSubmissions)} className="text-[#595959] hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* PRAYERS */}
              <TabsContent value="prayers">
                {prayers.length === 0 ? <div className="text-center py-10"><Heart className="w-10 h-10 text-[#E5E2DC] mx-auto mb-3" /><p className="text-[#595959]">No prayer requests.</p></div> : (
                  <div className="space-y-3">
                    {prayers.map(p => (
                      <div key={p.id} className="flex items-start justify-between bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4">
                        <div className="flex-1 mr-4">
                          <p className="font-medium text-sm text-[#1C2E4A]">{p.name} {p.is_private && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded ml-2">Private</span>}</p>
                          <p className="text-xs text-[#595959] mt-1">{p.request}</p>
                          <p className="text-xs text-[#595959]/50 mt-1">{new Date(p.timestamp).toLocaleDateString()}</p>
                        </div>
                        <button onClick={() => deleteItem("prayer-request", p.id, setPrayers)} className="text-[#595959] hover:text-red-500 p-1 shrink-0"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* NEWSLETTER */}
              <TabsContent value="newsletter">
                {newsletter.length === 0 ? <p className="text-center text-[#595959] py-10">No subscribers yet.</p> : (
                  <div className="border border-[#E5E2DC] rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader><TableRow className="bg-[#FAF9F6]">
                        <TableHead className="font-semibold">Email</TableHead>
                        <TableHead className="font-semibold">Subscribed</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {newsletter.map((n, i) => (
                          <TableRow key={i}>
                            <TableCell>{n.email}</TableCell>
                            <TableCell className="text-[#595959] text-sm">{new Date(n.subscribed_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* PRICING / ADD-ONS */}
              <TabsContent value="addons">
                <p className="text-sm text-[#595959] mb-4">Adjust pricing and order for any add-on. Use arrows to reorder. Changes take effect immediately for new customers.</p>
                <div className="space-y-3">
                  {addonsList.map((addon, idx) => (
                    <div key={addon.id} data-testid={`admin-addon-${addon.id}`} className="bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          {/* Move arrows */}
                          <div className="flex flex-col gap-0.5">
                            <button
                              disabled={idx === 0}
                              onClick={async () => {
                                const newList = [...addonsList];
                                [newList[idx - 1], newList[idx]] = [newList[idx], newList[idx - 1]];
                                setAddonsList(newList);
                                try {
                                  await axios.post(`${API}/addons/reorder`, { order: newList.map(a => a.id) });
                                } catch {}
                              }}
                              className="p-0.5 text-[#595959] hover:text-[#1C2E4A] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                              title="Move up"
                            >
                              <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              disabled={idx === addonsList.length - 1}
                              onClick={async () => {
                                const newList = [...addonsList];
                                [newList[idx], newList[idx + 1]] = [newList[idx + 1], newList[idx]];
                                setAddonsList(newList);
                                try {
                                  await axios.post(`${API}/addons/reorder`, { order: newList.map(a => a.id) });
                                } catch {}
                              }}
                              className="p-0.5 text-[#595959] hover:text-[#1C2E4A] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                              title="Move down"
                            >
                              <ArrowDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <span className={`w-2.5 h-2.5 rounded-full ${addon.active ? "bg-green-500" : "bg-gray-300"}`} />
                          <p className="font-medium text-sm text-[#1C2E4A]">{addon.name}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1" title="Regular price">
                            <span className="text-xs text-[#595959]">Reg $</span>
                            <input
                              type="number"
                              data-testid={`admin-addon-price-${addon.id}`}
                              value={addon.price}
                              onChange={e => {
                                const newPrice = parseFloat(e.target.value) || 0;
                                setAddonsList(prev => prev.map(a => a.id === addon.id ? {...a, price: newPrice} : a));
                              }}
                              className="w-20 border border-[#E5E2DC] bg-white rounded px-2 py-1 text-sm text-right"
                            />
                          </div>
                          <div className="flex items-center gap-1" title="Sale price (leave 0 for no sale)">
                            <span className="text-xs text-[#C8A97E]">Sale $</span>
                            <input
                              type="number"
                              data-testid={`admin-addon-sale-${addon.id}`}
                              value={addon.sale_price || 0}
                              onChange={e => {
                                const sp = parseFloat(e.target.value) || 0;
                                setAddonsList(prev => prev.map(a => a.id === addon.id ? {...a, sale_price: sp} : a));
                              }}
                              className="w-20 border border-[#C8A97E]/30 bg-white rounded px-2 py-1 text-sm text-right"
                            />
                          </div>
                          <button
                            data-testid={`admin-addon-save-${addon.id}`}
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/addons/${addon.id}`, { price: addon.price, sale_price: addon.sale_price || 0, active: addon.active });
                                toast.success(`${addon.name} updated`);
                              } catch { toast.error("Failed to update"); }
                            }}
                            className="text-xs bg-[#1C2E4A] text-white px-3 py-1 rounded hover:bg-[#152238]"
                          >Save</button>
                          <button
                            onClick={async () => {
                              const newActive = !addon.active;
                              try {
                                await axios.put(`${API}/addons/${addon.id}`, { active: newActive });
                                setAddonsList(prev => prev.map(a => a.id === addon.id ? {...a, active: newActive} : a));
                                toast.success(`${addon.name} ${newActive ? "enabled" : "disabled"}`);
                              } catch { toast.error("Failed to update"); }
                            }}
                            className={`text-xs px-3 py-1 rounded ${addon.active ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-green-50 text-green-600 hover:bg-green-100"}`}
                          >{addon.active ? "Disable" : "Enable"}</button>
                        </div>
                      </div>
                      <p className="text-xs text-[#595959] ml-12">{addon.description}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* SCHOOLS */}
              <TabsContent value="schools">
                <form onSubmit={addSchool} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 mb-6">
                  <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Add School</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm mb-1 block">School Name *</Label>
                      <Input value={schoolForm.name} onChange={e => setSchoolForm({...schoolForm, name: e.target.value})} placeholder="e.g. Grace Community Church School" className="border-[#E5E2DC] bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Website URL *</Label>
                      <Input value={schoolForm.website_url} onChange={e => setSchoolForm({...schoolForm, website_url: e.target.value})} placeholder="https://..." className="border-[#E5E2DC] bg-white" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm mb-1 block">Description</Label>
                      <Textarea value={schoolForm.description} onChange={e => setSchoolForm({...schoolForm, description: e.target.value})} placeholder="Brief description of the school" className="border-[#E5E2DC] bg-white resize-none" rows={3} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm mb-1 block">Tags (comma separated)</Label>
                      <Input value={schoolForm.tags} onChange={e => setSchoolForm({...schoolForm, tags: e.target.value})} placeholder="e.g. K-12, Alabama, Transcripts & Diplomas" className="border-[#E5E2DC] bg-white" />
                    </div>
                  </div>
                  <button type="submit" className="mt-4 bg-[#1C2E4A] text-white hover:bg-[#152238] px-6 py-2 rounded-md text-sm font-medium">Add School</button>
                </form>
                {schoolsList.length === 0 ? <p className="text-center text-[#595959] py-10">No schools listed yet. Add one above.</p> : (
                  <div className="space-y-3">
                    {schoolsList.map(school => (
                      <div key={school.id} className="bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 mr-4">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`w-2.5 h-2.5 rounded-full ${school.active ? "bg-green-500" : "bg-gray-300"}`} />
                              <p className="font-medium text-sm text-[#1C2E4A]">{school.name}</p>
                            </div>
                            <a href={school.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#C8A97E] hover:underline">{school.website_url}</a>
                            <p className="text-xs text-[#595959] mt-1 line-clamp-2">{school.description}</p>
                            {school.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {school.tags.map((tag, j) => (
                                  <span key={j} className="text-[10px] bg-[#1C2E4A]/5 text-[#1C2E4A] px-2 py-0.5 rounded-full">{tag}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={async () => {
                                const newActive = !school.active;
                                try {
                                  await axios.put(`${API}/schools/${school.id}`, { active: newActive });
                                  setSchoolsList(prev => prev.map(s => s.id === school.id ? {...s, active: newActive} : s));
                                  toast.success(`${school.name} ${newActive ? "visible" : "hidden"} on Our Schools page`);
                                } catch { toast.error("Failed to update"); }
                              }}
                              className={`text-xs px-3 py-1 rounded ${school.active ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-green-50 text-green-600 hover:bg-green-100"}`}
                            >{school.active ? "Hide" : "Show"}</button>
                            <button onClick={() => deleteItem("schools", school.id, setSchoolsList)} className="text-[#595959] hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* BLOG */}
              <TabsContent value="blog">
                {/* Generate */}
                <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 mb-6">
                  <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Generate Blog Post (AI)</h3>
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <Label className="text-sm mb-1 block">Topic</Label>
                      <Input value={blogTopic} onChange={e => setBlogTopic(e.target.value)} placeholder="e.g. Benefits of church school for homeschool families" className="border-[#E5E2DC] bg-white" />
                    </div>
                    <button
                      onClick={async () => {
                        if (!blogTopic.trim()) return toast.error("Enter a topic.");
                        setBlogGenerating(true);
                        try {
                          const res = await axios.post(`${API}/blog/generate`, { topic: blogTopic });
                          setBlogPosts(prev => [res.data, ...prev]);
                          setBlogTopic("");
                          setEditingPost(res.data.id);
                          toast.success("Blog post generated! Review and publish below.");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to generate.");
                        } finally { setBlogGenerating(false); }
                      }}
                      disabled={blogGenerating}
                      data-testid="blog-generate-btn"
                      className="bg-[#C8A97E] text-white hover:bg-[#B3956A] px-6 py-2 rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2 shrink-0"
                    >
                      {blogGenerating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</> : "Generate"}
                    </button>
                  </div>
                </div>

                {/* Posts List */}
                {blogPosts.length === 0 ? <p className="text-center text-[#595959] py-10">No blog posts yet. Generate one above.</p> : (
                  <div className="space-y-4">
                    {blogPosts.map(post => (
                      <div key={post.id} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-5">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 mr-4">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-sm text-[#1C2E4A]">{post.title}</p>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${post.status === "published" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                                {post.status}
                              </span>
                              {post.featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C8A97E]/10 text-[#C8A97E] font-medium">Featured</span>}
                            </div>
                            <p className="text-xs text-[#595959]">{post.excerpt}</p>
                            <p className="text-[10px] text-[#595959]/50 mt-1">Topic: {post.topic} &middot; {new Date(post.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button onClick={() => setEditingPost(editingPost === post.id ? null : post.id)} className="text-xs bg-[#1C2E4A] text-white px-3 py-1 rounded hover:bg-[#152238]">
                              {editingPost === post.id ? "Close" : "Edit"}
                            </button>
                            {post.status === "draft" ? (
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/blog/${post.id}`, { status: "published" });
                                    setBlogPosts(prev => prev.map(p => p.id === post.id ? {...p, status: "published"} : p));
                                    toast.success("Published!");
                                  } catch { toast.error("Failed to publish."); }
                                }}
                                className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                              >Publish</button>
                            ) : (
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/blog/${post.id}`, { status: "draft" });
                                    setBlogPosts(prev => prev.map(p => p.id === post.id ? {...p, status: "draft"} : p));
                                    toast.success("Unpublished.");
                                  } catch { toast.error("Failed."); }
                                }}
                                className="text-xs bg-yellow-100 text-yellow-700 px-3 py-1 rounded hover:bg-yellow-200"
                              >Unpublish</button>
                            )}
                            <button
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/blog/${post.id}`, { featured: !post.featured });
                                  setBlogPosts(prev => prev.map(p => p.id === post.id ? {...p, featured: !p.featured} : p));
                                  toast.success(post.featured ? "Unfeatured" : "Featured!");
                                } catch { toast.error("Failed."); }
                              }}
                              className={`text-xs px-3 py-1 rounded ${post.featured ? "bg-[#C8A97E]/10 text-[#C8A97E]" : "bg-gray-100 text-gray-600"}`}
                            >{post.featured ? "Unfeature" : "Feature"}</button>
                            <button onClick={() => deleteItem("blog", post.id, setBlogPosts)} className="text-[#595959] hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>

                        {/* Inline Editor */}
                        {editingPost === post.id && (
                          <div className="mt-4 pt-4 border-t border-[#E5E2DC] space-y-3">
                            <div>
                              <Label className="text-xs mb-1 block">Title</Label>
                              <Input value={post.title} onChange={e => setBlogPosts(prev => prev.map(p => p.id === post.id ? {...p, title: e.target.value} : p))} className="border-[#E5E2DC] bg-white" />
                            </div>
                            <div>
                              <Label className="text-xs mb-1 block">Excerpt</Label>
                              <Input value={post.excerpt} onChange={e => setBlogPosts(prev => prev.map(p => p.id === post.id ? {...p, excerpt: e.target.value} : p))} className="border-[#E5E2DC] bg-white" />
                            </div>
                            <div>
                              <Label className="text-xs mb-1 block">Featured Image URL</Label>
                              <Input value={post.featured_image || ""} onChange={e => setBlogPosts(prev => prev.map(p => p.id === post.id ? {...p, featured_image: e.target.value} : p))} placeholder="https://..." className="border-[#E5E2DC] bg-white" />
                            </div>
                            <div>
                              <Label className="text-xs mb-1 block">Content (Markdown)</Label>
                              <Textarea value={post.content} onChange={e => setBlogPosts(prev => prev.map(p => p.id === post.id ? {...p, content: e.target.value} : p))} rows={12} className="border-[#E5E2DC] bg-white resize-y font-mono text-xs" />
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/blog/${post.id}`, { title: post.title, excerpt: post.excerpt, content: post.content, featured_image: post.featured_image || "" });
                                  toast.success("Saved!");
                                  setEditingPost(null);
                                } catch { toast.error("Failed to save."); }
                              }}
                              className="bg-[#1C2E4A] text-white hover:bg-[#152238] px-6 py-2 rounded-md text-sm font-medium"
                            >Save Changes</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* LIVE USERS */}
              <TabsContent value="live">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-[#595959]">Visitors currently on the site (updates every 30 seconds).</p>
                  <button onClick={async () => {
                    const res = await axios.get(`${API}/presence/details`).catch(() => ({ data: [] }));
                    setLiveUsers(res.data);
                    toast.success("Refreshed");
                  }} className="text-xs bg-[#1C2E4A] text-white px-3 py-1.5 rounded hover:bg-[#152238] flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                </div>
                {liveUsers.length === 0 ? (
                  <div className="text-center py-16">
                    <Users className="w-10 h-10 text-[#E5E2DC] mx-auto mb-3" />
                    <p className="text-[#595959]">No active visitors right now.</p>
                  </div>
                ) : (
                  <div className="border border-[#E5E2DC] rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#FAF9F6]">
                          <TableHead className="font-semibold">Visitor</TableHead>
                          <TableHead className="font-semibold">Page</TableHead>
                          <TableHead className="font-semibold hidden md:table-cell">Device</TableHead>
                          <TableHead className="font-semibold hidden md:table-cell">Location</TableHead>
                          <TableHead className="font-semibold">Last Seen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {liveUsers.map((u, i) => (
                          <TableRow key={u.session_id || i}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-green-500 rounded-full" />
                                <div>
                                  <p className="text-sm font-medium text-[#1C2E4A]">
                                    {u.authenticated ? (u.user_name || "Member") : "Visitor"}
                                  </p>
                                  <p className="text-[10px] text-[#595959]/50">{u.ip}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-[#595959]">{u.current_page}</TableCell>
                            <TableCell className="text-xs text-[#595959] hidden md:table-cell">
                              <span className="flex items-center gap-1"><Monitor className="w-3 h-3" />{u.device} / {u.browser}</span>
                            </TableCell>
                            <TableCell className="text-xs text-[#595959] hidden md:table-cell">
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{u.city ? `${u.city}, ` : ""}{u.country}</span>
                            </TableCell>
                            <TableCell className="text-xs text-[#595959]">
                              {u.last_seen ? new Date(u.last_seen).toLocaleTimeString() : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* WEB STATS */}
              <TabsContent value="stats">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-[#595959]">Visitor analytics and traffic breakdown.</p>
                  <div className="flex items-center gap-2">
                    {[24, 168, 720].map(h => (
                      <button
                        key={h}
                        onClick={async () => {
                          setStatsHours(h);
                          const res = await axios.get(`${API}/presence/history?hours=${h}`).catch(() => ({ data: { sessions: [] } }));
                          setWebStats(res.data);
                        }}
                        className={`text-xs px-3 py-1.5 rounded ${statsHours === h ? "bg-[#1C2E4A] text-white" : "bg-[#FAF9F6] text-[#595959] border border-[#E5E2DC]"}`}
                      >
                        {h === 24 ? "24h" : h === 168 ? "7d" : "30d"}
                      </button>
                    ))}
                  </div>
                </div>
                {(() => {
                  const sessions = webStats?.sessions || [];
                  const uniqueIPs = new Set(sessions.map(s => s.ip)).size;
                  const pages = {};
                  const countries = {};
                  const devices = {};
                  const browsers = {};
                  const hours = {};
                  sessions.forEach(s => {
                    // Pages
                    const pg = s.current_page || "/";
                    pages[pg] = (pages[pg] || 0) + 1;
                    // Countries
                    const co = s.country || "Unknown";
                    countries[co] = (countries[co] || 0) + 1;
                    // Devices
                    const dv = s.device || "Unknown";
                    devices[dv] = (devices[dv] || 0) + 1;
                    // Browsers
                    const br = s.browser || "Unknown";
                    browsers[br] = (browsers[br] || 0) + 1;
                    // Hours
                    if (s.created_at) {
                      const hr = new Date(s.created_at).getHours();
                      hours[hr] = (hours[hr] || 0) + 1;
                    }
                  });
                  const sortedPages = Object.entries(pages).sort((a, b) => b[1] - a[1]).slice(0, 10);
                  const sortedCountries = Object.entries(countries).sort((a, b) => b[1] - a[1]).slice(0, 10);
                  const maxHourVal = Math.max(...Object.values(hours), 1);

                  return (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-5 text-center">
                          <p className="text-2xl font-bold text-[#1C2E4A]">{sessions.length}</p>
                          <p className="text-xs text-[#595959] mt-1">Total Visits</p>
                        </div>
                        <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-5 text-center">
                          <p className="text-2xl font-bold text-[#1C2E4A]">{uniqueIPs}</p>
                          <p className="text-xs text-[#595959] mt-1">Unique Visitors</p>
                        </div>
                        <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-5 text-center">
                          <p className="text-2xl font-bold text-[#1C2E4A]">{Object.keys(pages).length}</p>
                          <p className="text-xs text-[#595959] mt-1">Pages Visited</p>
                        </div>
                        <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-5 text-center">
                          <p className="text-2xl font-bold text-[#1C2E4A]">{Object.keys(countries).length}</p>
                          <p className="text-xs text-[#595959] mt-1">Countries</p>
                        </div>
                      </div>

                      {/* Traffic by Hour */}
                      <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6">
                        <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-4">Traffic by Hour</h4>
                        <div className="flex items-end gap-1 h-32">
                          {Array.from({ length: 24 }, (_, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                              <div
                                className="w-full bg-[#C8A97E] rounded-t min-h-[2px] transition-all"
                                style={{ height: `${((hours[i] || 0) / maxHourVal) * 100}%` }}
                                title={`${i}:00 — ${hours[i] || 0} visits`}
                              />
                              {i % 4 === 0 && <span className="text-[8px] text-[#595959]/50">{i}</span>}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Top Pages + Countries */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6">
                          <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-4">Top Pages</h4>
                          <div className="space-y-2">
                            {sortedPages.map(([page, count]) => (
                              <div key={page} className="flex items-center justify-between text-sm">
                                <span className="text-[#595959] truncate max-w-[200px]">{page}</span>
                                <span className="text-xs font-medium text-[#1C2E4A] bg-white px-2 py-0.5 rounded">{count}</span>
                              </div>
                            ))}
                            {sortedPages.length === 0 && <p className="text-xs text-[#595959]">No data yet.</p>}
                          </div>
                        </div>
                        <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6">
                          <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-4">Top Countries</h4>
                          <div className="space-y-2">
                            {sortedCountries.map(([country, count]) => (
                              <div key={country} className="flex items-center justify-between text-sm">
                                <span className="text-[#595959]">{country}</span>
                                <span className="text-xs font-medium text-[#1C2E4A] bg-white px-2 py-0.5 rounded">{count}</span>
                              </div>
                            ))}
                            {sortedCountries.length === 0 && <p className="text-xs text-[#595959]">No data yet.</p>}
                          </div>
                        </div>
                      </div>

                      {/* Device + Browser */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6">
                          <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-4">Devices</h4>
                          <div className="space-y-2">
                            {Object.entries(devices).sort((a, b) => b[1] - a[1]).map(([d, c]) => (
                              <div key={d} className="flex items-center justify-between text-sm">
                                <span className="text-[#595959]">{d}</span>
                                <span className="text-xs font-medium text-[#1C2E4A] bg-white px-2 py-0.5 rounded">{c}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6">
                          <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-4">Browsers</h4>
                          <div className="space-y-2">
                            {Object.entries(browsers).sort((a, b) => b[1] - a[1]).map(([b, c]) => (
                              <div key={b} className="flex items-center justify-between text-sm">
                                <span className="text-[#595959]">{b}</span>
                                <span className="text-xs font-medium text-[#1C2E4A] bg-white px-2 py-0.5 rounded">{c}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </section>
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[#595959]/60 shrink-0 min-w-[90px] capitalize">{label}:</span>
      <span className={`text-[#1C2E4A] font-medium break-all ${
        highlight === "green" ? "text-green-700" : highlight === "yellow" ? "text-yellow-700" : ""
      }`}>{value}</span>
    </div>
  );
}

function DataSection({ title, data }) {
  if (!data || typeof data !== "object") return null;
  const entries = Object.entries(data).filter(([k, v]) => {
    if (!v || !String(v).trim()) return false;
    if (typeof v === "boolean") return false; // hide help_* flags
    if (k.startsWith("help_") || k === "help") return false;
    return true;
  });
  if (entries.length === 0) return null;
  return (
    <div className="mb-6">
      <h4 className="text-xs uppercase tracking-wider text-[#C8A97E] font-semibold mb-3">{title}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {entries.map(([k, v]) => (
          <InfoRow key={k} label={k.replace(/_/g, " ")} value={String(v)} />
        ))}
      </div>
    </div>
  );
}
